import React, { createContext, useContext, useEffect } from 'react';
import { useTranslation, I18nextProvider } from 'react-i18next';

import { Language } from '../interfaces/language.type';
import { initI18n } from './i18n';

export interface LocalizationProviderProps {
  language?: Language;
  children: React.ReactNode;
}

interface LocalizationContextData {
  language: Language;
}

const LocalizationContext = createContext<LocalizationContextData>({} as LocalizationContextData);

const LocalizationProviderCtx = ({
  children,
  language = Language.EN,
}: LocalizationProviderProps) => {
  const { i18n, ready: i18nReady } = useTranslation();

  useEffect(() => {
    initI18n(language);
  }, [language]);

  useEffect(() => {
    if (!i18nReady) {
      return;
    }

    i18n.changeLanguage(language ?? Language.EN);
  }, [i18n, language, i18nReady]);

  return (
    <LocalizationContext.Provider value={{ language }}>
      <I18nextProvider i18n={i18n}>{children}</I18nextProvider>
    </LocalizationContext.Provider>
  );
};

export const LocalizationProvider: React.FC<LocalizationProviderProps> = ({
  children,
  language = Language.EN,
}) => {
  return <LocalizationProviderCtx language={language}>{children}</LocalizationProviderCtx>;
};

export function useLocalization(): LocalizationContextData {
  const context = useContext(LocalizationContext);

  if (!context) {
    throw new Error('useLocalization must be used within an LocalizationContext');
  }

  return context;
}
